import { debounce, get } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { EmpPosition } from '~/data/employee';
import { UnitPosition } from '~/data/unitPositions';
import { ScheduleEmploymentFormUnitPositionDetails } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { useVisible } from '~/hooks/useVisible';
import { addToTotal } from '~/utils/helper';

export type UseUnitPositionComponentProps = {
  empData: EmpPosition;
  unitPositions: UnitPosition[];
  index: number;
};

export const useUnitPositionComponent = (
  props: UseUnitPositionComponentProps,
) => {
  const { index, empData, unitPositions } = props;

  const { isVisible, toggle } = useVisible();

  const form = useFormContext<Form>();
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = form;

  const { fields } = useFieldArray<
    Form,
    'schedule.0.empPosition.unitPositionDetails'
  >({
    control,
    name: `schedule.${index}.empPosition.unitPositionDetails` as 'schedule.0.empPosition.unitPositionDetails',
  });

  useWatch({
    control,
    name: `schedule.${index}.empPosition.fInternalPositionUnit`,
  });

  const fInternalPositionUnitValue = getValues(
    `schedule.${index}.empPosition.fInternalPositionUnit`,
  );

  const { unitPositionOptions, unitPositionsDisabled } = useMemo(() => {
    return {
      unitPositionOptions: unitPositions.filter(
        (up) =>
          up.fUnitPositionUnitFk === fInternalPositionUnitValue ||
          (!fInternalPositionUnitValue &&
            up.fUnitPositionUnitFk === empData.fInternalPositionUnit),
      ),
      unitPositionsDisabled: fInternalPositionUnitValue === -1,
    };
  }, [
    unitPositions,
    fInternalPositionUnitValue,
    empData.fInternalPositionUnit,
  ]);

  const selectedUnitPositionValue = useMemo(() => {
    const fieldUnitPositionIds = fields.map((field) => field.unitPosition);

    return (
      unitPositionOptions.filter((up) =>
        fieldUnitPositionIds.includes(up.fUnitPositionId),
      ) || []
    );
  }, [fields, unitPositionOptions]);

  const onChangeUnitPositions = useCallback(
    (newUnitPositionIds: number[]) => {
      const unitPositionDetails = getValues(
        `schedule.${index}.empPosition.unitPositionDetails`,
      );

      const retainedUnitPositions = unitPositionDetails.filter(
        ({ unitPosition }) => newUnitPositionIds.includes(unitPosition),
      );
      const retainedUnitPositionIds = retainedUnitPositions.map(
        (up) => up.unitPosition,
      );
      const appendUnitPosition = newUnitPositionIds
        .filter((upId) => !retainedUnitPositionIds.includes(upId))
        .map((upId): ScheduleEmploymentFormUnitPositionDetails => {
          return {
            unitPosition: upId,
            unitPositionPercent: 0,
          };
        });
      const newUnitPositionDetails: ScheduleEmploymentFormUnitPositionDetails[] =
        [...retainedUnitPositions, ...appendUnitPosition];

      const total = newUnitPositionDetails.reduce((total: number, up) => {
        return addToTotal(total, up.unitPositionPercent);
      }, 0);

      setValue(
        `schedule.${index}.empPosition.unitPositionDetails`,
        newUnitPositionDetails,
      );

      setValue(
        `schedule.${index}.empPosition.totalUnitPositionPercentage`,
        total,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    },
    [getValues, setValue, index],
  );

  const hasTotalPercentageError = !!get(
    errors,
    `schedule.${index}.empPosition.totalUnitPositionPercentage`,
  );

  const isCollapsedContentOpen =
    (fields.length > 0 && isVisible) ||
    fields.length >= 2 ||
    hasTotalPercentageError;

  const isCollapsedContentToggleDisabled =
    fields.length === 0 || fields.length >= 2;

  const getUnitPositionInfo = useCallback(
    (unitPositionId: number) => {
      const foundUnitPosition = unitPositions.find(
        (up) => up.fUnitPositionId === unitPositionId,
      );

      if (!!foundUnitPosition) {
        const { fUnitPositionName, fUnitPositionCustomData } =
          foundUnitPosition;

        return {
          name: fUnitPositionName,
          customData: fUnitPositionCustomData,
        };
      }

      return {
        name: '',
        customData: {},
      };
    },
    [unitPositions],
  );

  const recalculateTotal = useMemo(
    () =>
      debounce(() => {
        const unitPositionDetails = getValues(
          `schedule.${index}.empPosition.unitPositionDetails`,
        );

        const total = unitPositionDetails.reduce((total: number, up) => {
          return addToTotal(total, up.unitPositionPercent);
        }, 0);

        setValue(
          `schedule.${index}.empPosition.totalUnitPositionPercentage`,
          total,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      }, 600),
    [index, getValues, setValue],
  );

  return {
    index,
    fields,
    form,
    collapsedContent: {
      isOpen: isCollapsedContentOpen,
      isToggleDisabled: isCollapsedContentToggleDisabled,
      toggle,
      hasError: hasTotalPercentageError,
    },
    onChangeUnitPositions,
    selectedUnitPositionValue,
    unitPositionOptions,
    unitPositionsDisabled,
    getUnitPositionInfo,
    recalculateTotal,
  };
};
